// Chốt chặn supply-chain cho command path trong pack settings: kiểm hook/statusLine
// command có trỏ path RA NGOÀI workspace không (absolute / traversal). Tách từ
// merge-pack-settings-into-project-settings.ts (A6 refactor) — GIỮ NGUYÊN hành vi.
// isCommandPathWithinWorkspace re-export qua barrel (public); isStatusLineCommandResolvable
// export cho cross-module (field-mergers/barrel dùng), KHÔNG re-export → không public.

import { join } from "node:path";
import { pathExists } from "./filesystem-helpers.js";
import { isPathInsideOrSameDirectory } from "./is-path-inside-or-same-directory.js";
import { log } from "./terminal-logger.js";

// Token "trông giống path" cần kiểm shape: absolute unix (`/...`), home (`~/...`),
// relative-explicit (`./...`, `../...`), Windows drive letter (`C:\...`), hoặc
// chứa backslash (Windows path separator). Token KHÔNG khớp pattern này (vd
// tên interpreter "node", "npx", flag "--foo", hay path tương đối kiểu
// ".claude/hooks/x.py" không có "./" prefix) được xử lý riêng bên dưới.
const PATH_LIKE_TOKEN = /^(\/|~\/|\.\.?\/|[A-Za-z]:[\\/]|.*\\)/;

// Kiểm 1 token path-like có SHAPE an toàn không (không absolute, không traversal
// ra ngoài workspace). Dùng chung cho mọi token nghi ngờ trong command.
function checkPathToken(
  workspacePath: string,
  filePath: string,
): { safe: boolean; reason?: string } {
  if (filePath.includes("${CLAUDE_PROJECT_DIR}")) {
    // Biến expand runtime, gốc luôn là workspace — an toàn về shape, trừ khi
    // cố lách bằng "..".
    if (filePath.includes("..")) {
      return { safe: false, reason: `path traversal trong "${filePath}"` };
    }
    return { safe: true };
  }
  if (
    filePath.startsWith("/") ||
    filePath.startsWith("~/") ||
    /^[A-Za-z]:[\\/]/.test(filePath) // Windows drive letter absolute path
  ) {
    return {
      safe: false,
      reason: `absolute path "${filePath}" (chỉ accept relative tới workspace)`,
    };
  }
  // v3.19.3: helper cross-platform thay cho so prefix `${wsResolved}/` — separator
  // hardcode POSIX khiến MỌI path hợp lệ bị reject trên Windows (path.resolve trả
  // `\`), vd statusLine "node .claude/statusline.cjs" không bao giờ merge được.
  // Ngữ nghĩa giữ nguyên: trong workspace HOẶC chính workspace → safe.
  if (!isPathInsideOrSameDirectory(workspacePath, join(workspacePath, filePath))) {
    return { safe: false, reason: `path "${filePath}" resolve ra ngoài workspace (traversal)` };
  }
  return { safe: true };
}

// SECURITY: command từ pack template/manifest có path file trỏ RA NGOÀI workspace
// không? Reject absolute path + path traversal (`../`). Đây là chốt chặn supply
// chain: pack compromise có thể inject hook trỏ `/etc/...` hoặc `../../evil`.
// KHÔNG check file tồn tại (đó là việc của isStatusLineCommandResolvable) — chỉ
// về SHAPE của path. Command shell thuần (echo/date) hoặc dùng ${CLAUDE_PROJECT_DIR}
// → coi là an toàn về path (biến expand runtime trong workspace).
//
// H1 fix (VULN-1 fail-open regression): version cũ chỉ check path SAU MỘT
// interpreter cố định trong danh sách (node|python|python3|bash|sh) — bất kỳ
// command dùng interpreter/path KHÁC (npx, node.exe, deno, hoặc path binary
// tuyệt đối không qua interpreter nào) đều lọt qua fail-open ({safe:true}).
// Giờ quét TẤT CẢ token trong command: token đầu tiên (có thể là binary tuyệt
// đối, vd "/usr/bin/evil") VÀ mọi token theo sau (có thể là script path sau
// 1 interpreter bất kỳ, vd "npx tsx /etc/evil.ts") đều bị kiểm shape nếu
// "trông giống path" (xem PATH_LIKE_TOKEN). Token không giống path (tên
// interpreter trần, flag, path tương đối không có "./" prefix) được bỏ qua —
// không đổi hành vi cho hook hợp lệ dạng "node .claude/hooks/x.py" hay
// "python3 ${CLAUDE_PROJECT_DIR}/.claude/pack/x.py".
// Trả { safe, reason } để caller log lý do cụ thể.
export function isCommandPathWithinWorkspace(
  workspacePath: string,
  command: string,
): { safe: boolean; reason?: string } {
  const tokens = command.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { safe: true };

  // Legacy behavior (giữ nguyên): "<interpreter> <path>" với interpreter tên
  // trần quen thuộc + path KHÔNG có "./"/"../" prefix (vd ".claude/hooks/x.py")
  // vẫn được resolve tương đối tới workspace như trước — không phá test cũ.
  const legacyMatch = command.trim().match(/^(node|python|python3|bash|sh)\s+([^\s]+)/);
  if (legacyMatch?.[2] && !PATH_LIKE_TOKEN.test(legacyMatch[2])) {
    const check = checkPathToken(workspacePath, legacyMatch[2]);
    if (!check.safe) return check;
  }

  // Quét MỌI token (kể cả token đầu — command không qua interpreter nào, vd
  // "/usr/bin/evil") xem có token nào "giống path" mà shape KHÔNG an toàn.
  for (const token of tokens) {
    if (!PATH_LIKE_TOKEN.test(token)) continue;
    const check = checkPathToken(workspacePath, token);
    if (!check.safe) return check;
  }

  return { safe: true };
}

// v1.7.0: Verify statusLine command file ref có thật trong workspace.
// Pack template thường ref `node .claude/statusline.cjs` hoặc shell command absolute.
// Parse simple: nếu là `node <path>` hoặc `python3 <path>` → check path tồn tại.
// Nếu là shell command khác (vd `echo`, `date`) → assume OK (không Avatar's concern).
export async function isStatusLineCommandResolvable(
  workspacePath: string,
  command: string,
): Promise<boolean> {
  // SHAPE check trước (absolute / traversal) — dùng chung với hook validation.
  const pathCheck = isCommandPathWithinWorkspace(workspacePath, command);
  if (!pathCheck.safe) {
    log.warn(`Pack statusLine reject: ${pathCheck.reason}.`);
    return false;
  }
  const match = command.trim().match(/^(node|python|python3|bash|sh)\s+([^\s]+)/);
  if (!match?.[2] || match[2].includes("${CLAUDE_PROJECT_DIR}")) {
    return true; // shell thuần hoặc runtime-var path — không check tồn tại tĩnh.
  }
  return await pathExists(join(workspacePath, match[2]));
}
